import type { FieldLibraryDocument } from '@/types'
import {
  typedCollection,
  typedDoc,
  getCollection,
  getDocument,
  updateDocument,
  deleteDocument,
  addDoc,
  serverTimestamp,
  orderBy,
  where,
} from '@/lib/firebase/firestore'
import type { FieldLibraryFormValues } from '../types'

const col = () => typedCollection<FieldLibraryDocument>('fieldLibrary')
const doc = (id: string) => typedDoc<FieldLibraryDocument>('fieldLibrary', id)

export const adminFieldLibraryService = {
  async getAll(): Promise<Array<FieldLibraryDocument & { id: string }>> {
    return getCollection(col(), orderBy('createdAt', 'desc'))
  },

  async getByCategory(category: string): Promise<Array<FieldLibraryDocument & { id: string }>> {
    return getCollection(col(), where('category', '==', category), orderBy('createdAt', 'desc'))
  },

  async getById(id: string): Promise<FieldLibraryDocument | null> {
    return getDocument(doc(id))
  },

  async create(data: FieldLibraryFormValues): Promise<string> {
    const payload = buildPayload(data)
    const ref = await addDoc(col(), { ...payload, createdAt: serverTimestamp(), updatedAt: serverTimestamp() } as unknown as FieldLibraryDocument)
    return ref.id
  },

  async update(id: string, data: FieldLibraryFormValues): Promise<void> {
    const payload = buildPayload(data)
    await updateDocument(doc(id), { ...payload, updatedAt: serverTimestamp() } as unknown as Partial<FieldLibraryDocument>)
  },

  async delete(id: string): Promise<void> {
    await deleteDocument(doc(id))
  },

  /** Seed the mailing list opt-in field if it doesn't exist yet. */
  async seedMailingListField(): Promise<void> {
    const existing = await getCollection(col(), where('fieldId', '==', 'mailing-list-opt-in'))
    if (existing.length > 0) return
    const payload: Omit<FieldLibraryDocument, 'id' | 'createdAt' | 'updatedAt'> = {
      fieldId: 'mailing-list-opt-in',
      type: 'mailing_list',
      label: 'Would you like to join the ODD mailing list?',
      description: 'We send occasional updates about festival news, ticket drops, calls for artists, and community events. No spam — unsubscribe anytime.',
      placeholder: 'Yes, please add me to the ODD mailing list.',
      category: 'General',
    }
    await addDoc(col(), {
      ...payload,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    } as unknown as FieldLibraryDocument)
  },

}

function buildPayload(data: FieldLibraryFormValues): Omit<FieldLibraryDocument, 'id' | 'createdAt' | 'updatedAt'> {
  return {
    fieldId: data.fieldId,
    type: data.type,
    label: data.label,
    ...(data.description ? { description: data.description } : {}),
    ...(data.placeholder ? { placeholder: data.placeholder } : {}),
    ...(data.category ? { category: data.category } : {}),
    ...(data.options.length > 0 ? { options: data.options } : {}),
    ...(data.min !== '' ? { min: data.min as number } : {}),
    ...(data.max !== '' ? { max: data.max as number } : {}),
    ...(data.minLabel ? { minLabel: data.minLabel } : {}),
    ...(data.integerOnly !== undefined ? { integerOnly: data.integerOnly } : {}),
    ...(data.maxLabel ? { maxLabel: data.maxLabel } : {}),
    ...(data.maxFileSizeMB !== '' ? { maxFileSizeMB: data.maxFileSizeMB as number } : {}),
    ...(data.acceptedFileTypes.length > 0 ? { acceptedFileTypes: data.acceptedFileTypes } : {}),
    ...(data.maxEntries !== '' ? { maxEntries: data.maxEntries as number } : {}),
  }
}
